
import { Vue, Component, Prop } from "vue-property-decorator";
import UiButton from "@/components/button/ui-button.vue";
import CustomRequestModal from "@/components/requests/CustomRequestModal.vue";
import {
  Custom_Request,
  Custom_Request_Insert_Input, Custom_Request_Skills,
  Project_Activity,
  Project_Skill
} from "@/generated/graphql";
import { NOTIFICATION } from "@/entities/Events";
import Message from "@/data/notificationsMessages";
import CustomRequestFormData from "@/entities/customRequests/CustomRequestFormData";
import CREATE_CUSTOM_REQUEST from "@/apollo/requests/CreateCustomRequest.gql"
import UPDATE_CUSTOM_REQUEST from "@/apollo/requests/UpdateFullCustomRequestData.gql"
import { customRequestCreatedMessage, customRequestUpdatedMessage } from "@/data/successMessages";

interface CreateRequestArgs {
  customRequestFormData: CustomRequestFormData,
  projectActivityId: string,
  companyId: string,
  currentUserId: string,
}

interface UpdateRequestArgs {
  customRequestFormData: CustomRequestFormData,
  customRequestSkills: Custom_Request_Skills[],
  currentUserId: string,
}

interface ReflectUiActionArgs {
  action: () => Promise<void>,
  actionSuccess: () => void,
}

@Component({
  components: { CustomRequestModal, UiButton }
})
export default class ActivityCustomRequest extends Vue {
  @Prop({ type: String, required: true }) readonly companyId!: string;
  @Prop({ type: String, required: true }) readonly currentUserId!: string;
  @Prop({ type: Array, required: true }) readonly projectSkills!: Project_Skill[];
  @Prop({ type: Object, required: true }) readonly projectActivity!: Project_Activity;

  modalShown: boolean = false;
  processing: boolean = false;

  get buttonText(): string {
    if (this.customRequest) {
      return "Update resource request";
    }

    return "Resource request";
  }

  get customRequest(): Custom_Request | undefined {
    return this.projectActivity.custom_requests[0];
  }

  get initialSkillIds(): number[] {
    if (this.customRequest) {
      return this.customRequest.custom_request_skills.map(({skill_id}) => skill_id);
    }

    return this.projectSkills.map(({ skill }) => skill.id);
  }

  getCustomRequestContent(activity: Project_Activity): Partial<Custom_Request> {
    if (this.customRequest) {
      return this.customRequest;
    }

    return {
      description: activity.note,
      job_title: activity.title,
    };
  }

  create(customRequestFormData: CustomRequestFormData) {
    return this.reflectUiAction({
      action: () => this._createRequest({
        customRequestFormData,
        projectActivityId: this.projectActivity.id,
        companyId: this.companyId,
        currentUserId: this.currentUserId,
      }),
      actionSuccess: () => {
        this.$eventBus.emit(NOTIFICATION, Message.successMessage(customRequestCreatedMessage));
        this.$emit("customRequestCreated");
      }
    });
  }

  update(customRequestFormData: CustomRequestFormData) {
    return this.reflectUiAction({
      action: () => this._updateRequest({
        customRequestFormData,
        customRequestSkills: this.customRequest?.custom_request_skills || [],
        currentUserId: this.currentUserId,
      }),
      actionSuccess: () => {
        this.$eventBus.emit(NOTIFICATION, Message.successMessage(customRequestUpdatedMessage));
        this.$emit("customRequestUpdated");
      }
    });
  }

  private async _createRequest({
     customRequestFormData,
     projectActivityId,
     companyId,
     currentUserId
   }: CreateRequestArgs): Promise<void> {

    const {
      description,
      skills,
      jobTitle,
    } = customRequestFormData;

    const data: Custom_Request_Insert_Input = {
      project_activity_id: projectActivityId,
      description,
      company_id: companyId,
      created_by_id: currentUserId,
      job_title: jobTitle,
      custom_request_skills: {
        data: skills.map(skillId => ({ skill_id: Number(skillId) })),
      },
    };

    await this.$apollo.mutate({
      mutation: CREATE_CUSTOM_REQUEST,
      variables: {
        object: data,
      }
    });
  }

  private async _updateRequest({
     customRequestFormData,
     customRequestSkills,
     currentUserId,
   }: UpdateRequestArgs): Promise<void> {
    const selectedSkills = customRequestFormData.skills;
    const {
      id: requestId,
      jobTitle,
      description
    } = customRequestFormData;

    const requestData: Partial<Custom_Request> = {
      job_title: jobTitle,
      updated_by_id: currentUserId,
      description,
    };

    const addedSkillIds: Partial<Custom_Request_Skills>[] = selectedSkills
      .filter(selectedSkillId => !customRequestSkills.some(({skill_id: skillId}) => skillId === selectedSkillId))
      .map(skillId => ({
        skill_id: skillId,
        custom_request_id: requestId,
      }));

    const deletedCustomRequestSkills: number[] = customRequestSkills
      .filter(({skill_id: skillId}) => !selectedSkills.some(selectedSkillId => skillId === selectedSkillId))
      .map(({id}) => id);

    await this.$apollo.mutate({
      mutation: UPDATE_CUSTOM_REQUEST,
      variables: {
        requestId,
        requestData,
        deletedCustomRequestSkills,
        addedSkillIds,
      },
    });
  }

  private async reflectUiAction({ action, actionSuccess }: ReflectUiActionArgs): Promise<void> {
    if (this.processing) {
      return;
    }

    this.processing = true;

    try {
      await action();
      actionSuccess();
      this.modalShown = false;
    } catch (_) {
      this.$eventBus.emit(NOTIFICATION, Message.errorMessage("Something went wrong!"));
    } finally {
      this.processing = false;
    }
  }
}
